
import {
  computed, defineComponent, inject, onMounted, PropType, ref, watch,
} from 'vue';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMainStore } from '@/store/mainStore';
import { useMessagesStore } from '../store/messagesStore';
import AppHeader from '@/components/AppHeader.vue';
import AppSearch from '@/components/UI/AppSearch.vue';
import ItemMixed from '@/modules/drive/components/items/ItemMixed.vue';
import AppNoMessage from '@/components/AppNoMessage.vue';
import MessagesService from '../services/MessagesService';
import { MESSAGE_SHARED_ITEM_TYPES, SHARE_ACCESS } from '@/types/enums';
import { IMixedData } from '@/types/interfaces';
import MultipleSelectionPopUp from '@/components/BottomPopUp/PopUpTypes/MultipleSelectionPopUp.vue';
import DriveService from '@/modules/drive/services/DriveService';
import UsePagination from '@/composables/UsePagination';
import { DIRECTION } from '@/helpers/isScrollEnd';
import DesktopHeader from '@/components/desktop/DesktopHeader.vue';
import AppLoader from '@/components/UI/AppLoader.vue';

export default defineComponent({
  emits: ['close'],
  components: {
    AppLoader,
    DesktopHeader,
    AppHeader,
    AppSearch,
    ItemMixed,
    MultipleSelectionPopUp,
    AppNoMessage,
  },
  props: {
    type: { type: String as PropType<MESSAGE_SHARED_ITEM_TYPES>, required: true },
  },
  setup(props) {
    const refMixed = ref();
    const refMixedSelectedMedia = computed(() => refMixed.value?.selectedGroupMedia);
    const showSettingsPopUp = computed(() => Boolean(refMixedSelectedMedia.value?.length));
    const messagesStore = useMessagesStore();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();

    const dataLoading = ref(true);
    const mixed = ref<IMixedData[]>([]);
    const isSettingsPopUp = ref(false);
    const showSelector = ref(false);
    const mainTitle = computed(() => {
      if (props.type === 'CASE_TYPE') {
        return 'Group Cases';
      }
      if (props.type === 'PHOTO_TYPE') {
        return 'Group Photos';
      }
      return 'Group Files';
    });

    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;
    const title = computed(() => $localize(`message.file.selector.popup.upload.${props.type.toLowerCase()}`) || 'Group Photos');

    const {
      scroller, pageIdx, isPageLoading, onScroll, setLastPage,
    } = UsePagination();

    const noMixedMessage = computed(() => {
      if (!mixed.value.length) {
        if (driveStore.search) {
          return {
            text: 'No search results found',
            icon: 'icon-not-found',
          };
        }
        return {
          text: 'No files here yet...',
          icon: 'icon-no-data',
        };
      }
      return '';
    });

    async function fetchData() {
      return MessagesService.fetchGroupSharedItems(messagesStore.currentConversation!.group!.id, props.type, pageIdx.value, driveStore.search)
        .then((response) => {
          const res = response.results.data.items.filter((mix) => !mix.deleted);
          if (pageIdx.value > 0) {
            mixed.value = [...res, ...mixed.value];
          } else {
            mixed.value = res;
          }
          return response;
        });
    }

    function handleScroll() {
      onScroll(fetchData, DIRECTION.BOTTOM, dataLoading.value);
    }

    function deleteSelectedItems() {
      if (props.type === MESSAGE_SHARED_ITEM_TYPES.CASE_TYPE) {
        const selectedCases = {
          ids: refMixedSelectedMedia.value.filter((item) => item.type === 'CASE').map((cases) => cases.id),
          accessType: SHARE_ACCESS.GROUP,
          target: messagesStore.currentConversation!.group!.id,
        };
        DriveService.removeBatchShare(selectedCases).then(() => {
          fetchData();
        });
      } else if (props.type === MESSAGE_SHARED_ITEM_TYPES.PHOTO_TYPE) {
        const selectedPhoto = {
          ids: refMixedSelectedMedia.value.filter((item) => item.type === 'FILE' && item.file.type === 'PHOTO').map((item) => item.file.id),
          accessType: SHARE_ACCESS.GROUP,
          target: messagesStore.currentConversation!.group!.id,
        };
        DriveService.removeBatchShare(selectedPhoto).then(() => {
          fetchData();
        });
      } else if (props.type === MESSAGE_SHARED_ITEM_TYPES.FILE_TYPE) {
        const selectedFiles = {
          ids: refMixedSelectedMedia.value.filter((item) => item.type === 'FILE' && item.file.type === 'FILE').map((files) => files.id),
          accessType: SHARE_ACCESS.GROUP,
          target: messagesStore.currentConversation!.group!.id,
        };
        DriveService.removeBatchShare(selectedFiles).then(() => {
          fetchData();
        });
      }
      showSelector.value = false;
      isSettingsPopUp.value = false;
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      timeout = setTimeout(() => {
        fetchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
          });
      }, 1000);
    });

    onMounted(() => {
      driveStore.search = '';
      fetchData()
        .then((response) => {
          setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
        })
        .finally(() => { dataLoading.value = false; });
    });

    return {
      driveStore,
      messagesStore,
      dataLoading,
      scroller,
      isPageLoading,
      handleScroll,
      deleteSelectedItems,
      mixed,
      refMixed,
      refMixedSelectedMedia,
      title,
      showSettingsPopUp,
      isSettingsPopUp,
      showSelector,
      noMixedMessage,
      mainStore,
      mainTitle,
    };
  },
});
